import { normalizeKortexResultItem } from "./kortexAnalysis";

export function safeParseAnalysis(value) {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return null;

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    return null;
  } catch {
    return null;
  }
}

/**
 * Normaliza o formato de `results` vindo do callback da Kortex.
 * - Formato esperado hoje no UI: array de itens { question_text, answer, ... }
 * - Formato alternativo: objeto { chave: valor } (ex.: { resumo: "..." })
 */
export function normalizeKortexResults(results) {
  if (!results) return [];

  if (Array.isArray(results)) {
    return results.map((item) => normalizeKortexResultItem(item));
  }

  if (typeof results === "object") {
    return Object.entries(results).map(([key, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return normalizeKortexResultItem(value, key);
      }
      return normalizeKortexResultItem({ answer: value }, key);
    });
  }

  return [];
}

/**
 * Extrai o resumo do processo a partir de `results`.
 * No formato atual do callback da Kortex, o resumo vem em `results.resumo`.
 */
export function extractResumoFromKortexResults(results) {
  if (!results) return null;

  // Formato histórico: array de itens { question_text: "...", answer: "..." }
  if (Array.isArray(results)) {
    const item = results.find((r) => {
      const q = (r?.question_text || "").toString().trim().toLowerCase();
      return q === "resumo" || q.startsWith("resumo");
    });
    const ans = item?.answer;
    return typeof ans === "string" && ans.trim() ? ans.trim() : null;
  }

  // Formato atual: objeto (ex.: { resumo: "...", ... })
  if (typeof results === "object") {
    const raw = results.resumo;
    if (typeof raw === "string" && raw.trim()) return raw.trim();

    if (raw && typeof raw === "object") {
      const inner = raw.answer ?? raw.value ?? raw.text;
      if (typeof inner === "string" && inner.trim()) return inner.trim();
    }
  }

  return null;
}

export function formatIsoToPtBR(isoString, { withTime = true } = {}) {
  if (!isoString) return "N/A";
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "N/A";
    return withTime
      ? date.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : date.toLocaleDateString("pt-BR");
  } catch {
    return "N/A";
  }
}

const EMAIL_RE_GLOBAL = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/**
 * Normaliza dígitos de telefone brasileiro (DDD + número, 10 ou 11 dígitos).
 */
export function normalizeBrazilPhoneDigits(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.startsWith("55") && d.length >= 12) d = d.slice(2);
  while (d.length > 11 && d.startsWith("0")) d = d.replace(/^0+/, "");
  if (d.length !== 10 && d.length !== 11) return null;

  const ddd = Number(d.slice(0, 2));
  if (!Number.isInteger(ddd) || ddd < 11 || ddd > 99) return null;

  const firstSubscriberDigit = d[2];
  if (d.length === 11 && firstSubscriberDigit !== "9") return null;
  if (d.length === 10 && !["2", "3", "4", "5"].includes(firstSubscriberDigit)) return null;

  return d;
}

export function formatBrazilPhoneDisplay(normalizedDigits) {
  const n = normalizeBrazilPhoneDigits(normalizedDigits);
  if (!n) return String(normalizedDigits || "").trim() || "—";
  if (n.length === 11) {
    return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
  }
  return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
}

function extractPhonesFromAnswerText(answerText) {
  const text = String(answerText || "");
  const normalizedKeys = new Set();
  const patterns = [
    /\+55\s*\(?(\d{2})\)?\s*(\d{4,5})\s*-?\s*(\d{4})/gi,
    /\((\d{2})\)\s*(\d{4,5})\s*-?\s*(\d{4})/g,
    /\b(\d{2})\s+(\d{4,5})\s*-?\s*(\d{4})\b/g,
    /(?:telefone|tel\.?|celular|cel\.?|whats(?:app)?|contato|fone)\s*[:\-]?\s*(?:\+?55\s*)?(\d{2})(\d{4,5})(\d{4})/gi,
  ];
  for (const rx of patterns) {
    let m;
    const r = new RegExp(rx.source, rx.flags);
    while ((m = r.exec(text))) {
      const joined = m.slice(1).join("");
      const norm = normalizeBrazilPhoneDigits(joined);
      if (norm) normalizedKeys.add(norm);
    }
  }
  return [...normalizedKeys];
}

/**
 * Extrai e-mails e telefones citados nas respostas do Q&A da Kortex (análise do processo).
 */
export function extractProcessContactHints(analysisObj) {
  if (!analysisObj || typeof analysisObj !== "object") {
    return { emails: [], phones: [], sources: [] };
  }

  const results = normalizeKortexResults(analysisObj.results);
  const emailSet = new Set();
  const phoneNormSet = new Set();
  const sources = [];

  for (const r of results) {
    const question = (r?.question_text || "").toString().trim();
    const answer = (r?.answer ?? "").toString();
    if (!answer.trim()) continue;

    const emails = (answer.match(EMAIL_RE_GLOBAL) || []).map((e) => e.trim().toLowerCase());
    const phoneNorms = extractPhonesFromAnswerText(answer);

    for (const e of emails) emailSet.add(e);
    for (const p of phoneNorms) phoneNormSet.add(p);

    if (emails.length > 0 || phoneNorms.length > 0) {
      sources.push({
        question: question || "Pergunta não informada",
        emails,
        phones: phoneNorms.map((d) => formatBrazilPhoneDisplay(d)),
      });
    }
  }

  return {
    emails: [...emailSet],
    phones: [...phoneNormSet].map((d) => formatBrazilPhoneDisplay(d)),
    sources,
  };
}


